import { Component, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ROUTE_PATHS } from '@/config/routes'
import { showWarning } from '@/lib/utils/notifications'

type Props = { children: ReactNode }

type State = {
  error: Error | null
}

/**
 * Catches render/lifecycle errors under the dashboard main column so a failed page
 * (e.g. bad API payload after Yardi retry polling) does not blank the whole shell.
 */
export class PageErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch() {
    showWarning('This page hit an unexpected error. Please try again or reload the page.')
  }

  private handleReset = () => {
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (error) {
      return (
        <div
          className="flex min-h-[50vh] flex-col items-center justify-center bg-gray-50 px-4 py-16"
          data-testid="page-error-boundary"
        >
          <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <h1 className="text-lg font-semibold text-slate-900">Something went wrong</h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              This page hit an unexpected error. You can try again, reload the page, or return to the
              dashboard.
            </p>
            {import.meta.env.DEV && (
              <p className="mt-4 break-all text-left font-mono text-xs text-red-700/90">{error.message}</p>
            )}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
              <button
                type="button"
                onClick={this.handleReset}
                className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
              >
                Try again
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:bg-slate-50"
              >
                Reload page
              </button>
              <Link
                to={ROUTE_PATHS.DASHBOARD}
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:bg-slate-50"
              >
                Go to dashboard
              </Link>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
